import { createContext, useCallback, useContext, useState } from "react";
import { CheckCircle2, AlertTriangle, Info, X } from "lucide-react";

const ToastCtx = createContext(null);

let _id = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const push = useCallback((toast) => {
    const id = ++_id;
    const t = { id, type: "info", title: "", message: "", duration: 4000, ...toast };
    setToasts((prev) => [...prev, t]);
    if (t.duration > 0) {
      setTimeout(() => setToasts((prev) => prev.filter((x) => x.id !== id)), t.duration);
    }
    return id;
  }, []);

  const dismiss = useCallback((id) => setToasts((prev) => prev.filter((x) => x.id !== id)), []);

  const toast = {
    info: (message, opts = {}) => push({ type: "info", message, ...opts }),
    success: (message, opts = {}) => push({ type: "success", message, ...opts }),
    error: (message, opts = {}) => push({ type: "error", message, ...opts, duration: opts.duration ?? 7000 }),
    warning: (message, opts = {}) => push({ type: "warning", message, ...opts }),
  };

  return (
    <ToastCtx.Provider value={{ toast, dismiss }}>
      {children}
      <div className="fixed top-4 right-4 z-[100] flex flex-col gap-2 max-w-md w-[90vw] sm:w-full pointer-events-none" data-testid="toast-stack">
        {toasts.map((t) => <ToastItem key={t.id} t={t} onClose={() => dismiss(t.id)} />)}
      </div>
    </ToastCtx.Provider>
  );
}

const META = {
  success: { Icon: CheckCircle2, color: "border-l-green-600 bg-green-50 text-green-900" },
  error:   { Icon: AlertTriangle, color: "border-l-red-600 bg-red-50 text-red-900" },
  warning: { Icon: AlertTriangle, color: "border-l-yellow-500 bg-yellow-50 text-yellow-900" },
  info:    { Icon: Info, color: "border-l-blue-600 bg-blue-50 text-blue-900" },
};

function ToastItem({ t, onClose }) {
  const meta = META[t.type] || META.info;
  return (
    <div
      data-testid={`toast-${t.type}`}
      role="alert"
      className={`pointer-events-auto es-card border-l-4 px-4 py-3 flex items-start gap-3 animate-fade-in ${meta.color}`}
    >
      <meta.Icon className="w-4 h-4 mt-0.5 flex-shrink-0" />
      <div className="flex-1 min-w-0">
        {t.title && <div className="font-semibold text-sm leading-tight">{t.title}</div>}
        <div className="text-sm leading-snug break-words">{t.message}</div>
      </div>
      {t.action && (
        <button
          data-testid={t.action.testid || "toast-action"}
          onClick={() => { try { t.action.onClick?.(); } finally { onClose(); } }}
          className="text-sm font-bold underline underline-offset-2 hover:no-underline whitespace-nowrap"
        >
          {t.action.label}
        </button>
      )}
      <button onClick={onClose} className="text-zinc-500 hover:text-zinc-900 -mr-1"><X className="w-4 h-4" /></button>
    </div>
  );
}

export function useToast() {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx.toast;
}
